/**
 * OCEAN share-link capture, and the switches for what is held back to V2.
 *
 * Who sees OCEAN is decided by the server, not here: Admin → Ocean → Feature
 * access ("ocean", Backend/feature_access.py), read through
 * utils/featureAccess.ts. App.tsx renders OceanApp only when that says the
 * account is enabled (super admins, selected admins or all admins until the
 * mode is "everyone"); everyone else keeps the Coming Soon page. The old switches — VITE_OCEAN_ENABLED at build time
 * and ?ocean_preview=1 per browser — are gone: either one let a member in.
 * The localhost dev harness (./dev) renders OceanApp directly and needs neither.
 *
 * Share links look like /?view=ocean&ocean=problem:<id>&s=<code> (s: the
 * tracked share code, Backend share_links); a shared collection's,
 * /?view=ocean&ocean=collection:<token>&s=<code>. Both are stashed here at module
 * load — before App's own deep-link handling, and surviving a sign-in round
 * trip — and taken once by the page. The Profile page's OCEAN tab uses the
 * same stash to open a saved card (openInOcean).
 */
const LEGACY_PREVIEW_KEY = 'ocean_preview';
const LINK_KEY = 'ocean_deeplink';
const LINK_TTL_MS = 30 * 60 * 1000;
const LINK_RE = /^(problem|company|solution|collection):([A-Za-z0-9._-]{1,128})$/;
const SAVED_FOCUS_KEY = 'ocean_saved_focus';
const CODE_RE = /^[a-z2-7]{8}$|^dev[0-9a-z]{1,8}$/;

/** Named collections: saving picks a collection (the last one used, with
 *  "Change"), cards move between them, and Profile → Saved → OCEAN manages and
 *  shares them (owner, 2026-10-30; they were held back to V2 on 2026-10-29). */
export const COLLECTIONS_ENABLED = true;

/** Sharing a whole collection by link is held back until its signed-out view
 *  exists (tracker, 2026-10-31): no Share button or menu item. A collection
 *  shared before keeps "Stop sharing", and its old links still open in OCEAN. */
export const COLLECTION_SHARING_ENABLED = false;

export type DeepLinkKind = 'problem' | 'company' | 'solution' | 'collection';
export interface DeepLink { kind: DeepLinkKind; id: string; share?: string }

const stash = (v: string, share?: string) =>
  sessionStorage.setItem(LINK_KEY, JSON.stringify({ v, share: share || undefined, at: Date.now() }));

// At module load, so the link survives the sign-in round trip (which drops the query).
try {
  // The retired per-browser preview switch: nothing reads it any more.
  localStorage.removeItem(LEGACY_PREVIEW_KEY);
  const params = new URLSearchParams(window.location.search);
  const v = params.get('ocean');
  const code = params.get('s') || '';
  if (v && LINK_RE.test(v)) stash(v, CODE_RE.test(code) ? code : undefined);
} catch { /* storage blocked: the link just opens Ocean's search */ }

/** Have OCEAN open this card the next time it mounts (the Profile tab's cards). */
export function openInOcean(kind: DeepLinkKind, id: string): void {
  try { stash(`${kind}:${id}`); } catch { /* storage blocked: OCEAN opens on its search */ }
}

export function takeDeepLink(): DeepLink | null {
  try {
    const raw = sessionStorage.getItem(LINK_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(LINK_KEY);
    const { v, share, at } = JSON.parse(raw);
    const m = typeof v === 'string' ? LINK_RE.exec(v) : null;
    if (!m || Date.now() - Number(at || 0) > LINK_TTL_MS) return null;
    return { kind: m[1] as DeepLinkKind, id: m[2], share: typeof share === 'string' ? share : undefined };
  } catch {
    return null;
  }
}

/** Profile → Saved → OCEAN opens on this collection the next time it mounts
 *  (OCEAN's rail and "View in Profile"); null = the collection list. */
export function focusSavedCollection(collectionId: number | null): void {
  try { sessionStorage.setItem(SAVED_FOCUS_KEY, JSON.stringify({ id: collectionId, at: Date.now() })); } catch { /* storage blocked */ }
}

/** Read without clearing (a StrictMode double render reads it twice); clearSavedFocus() after mount. */
export function peekSavedFocus(): { id: number | null } | null {
  try {
    const raw = sessionStorage.getItem(SAVED_FOCUS_KEY);
    if (!raw) return null;
    const { id, at } = JSON.parse(raw);
    if (Date.now() - Number(at || 0) > LINK_TTL_MS) return null;
    return { id: typeof id === 'number' ? id : null };
  } catch {
    return null;
  }
}

export function clearSavedFocus(): void {
  try { sessionStorage.removeItem(SAVED_FOCUS_KEY); } catch { /* storage blocked */ }
}
